import React, { useEffect, useMemo, useState } from "react";
import { JOURNEY_EMAIL_REGEX } from "../../../../utils/emailValidation";
import {
  Box,
  Tabs,
  Tab,
  Grid,
  Button,
  useMediaQuery,
  useTheme,
  Typography,
  Card,
  CircularProgress,
  Snackbar,
  Alert,
  Skeleton,
} from "@mui/material";
import { IconButton } from "../../../../components/ui/Button";
import { useDispatch, useSelector } from "react-redux";
import postsServices from "../../../../services/api/service/postsService";
import {
  setAadhaarApplicationNoCoApplicant,
} from "../../../../services/redux/slices/stepperDynamicFormSlice";
import { AppDispatch, RootState } from "../../../../services/redux/store";
import VerifiedIcon from "../../../../assets/svgIcons/check-circle.svg";
import RightBlackArrow from "../../../../assets/svgIcons/right-black-arrow.svg";
import idIcon from "../../../../assets/svgIcons/id.svg";
import {
  FirstCoApplicantEmploymentNotice,
  ensureFirstCoApplicantEmploymentYes,
  isFirstCoApplicant,
  lockFirstCoApplicantEmploymentField,
} from "../coApplicantEmploymentLock";
type SingleCoApplicantAdditionalDetailsProps = {
  fields: any[];
  renderField: (field: any) => React.ReactNode;
  values?: Record<string, any>;
  formikRef?:any,
};

const getFieldName = (field: any) => String(field?.name || field?.key || "");

const getAutocompleteOptionValue = (value: any): string => {
  if (value && typeof value === "object") {
    return String(value?.value ?? value?.label ?? "").trim();
  }

  return String(value ?? "").trim();
};

const getCoApplicantNumberFromFields = (fields: any[]): string | null => {
  const names = (fields || []).map(getFieldName);
  for (const name of names) {
    const match =
      name.match(/^basicCoApplicant(\d+)/i) ||
      name.match(/^addressCoApplicant(\d+)/i) ||
      name.match(/^employmentCoApplicant(\d+)/i) ||
      name.match(/^coApplicant(\d+)/i);
    if (match?.[1]) return match[1];
  }
  return null;
};

const isAddressField = (name: string) =>
  /^addressCoApplicant\d+/i.test(name);

const isEmploymentField = (name: string) =>
  /^employmentCoApplicant\d+/i.test(name);

// Helpers to match the exact new JSON field naming from CoApplicantAdditionalDetails
const isPermanentAddressField = (name: string) =>
  /^addressCoApplicant\d+permanent/i.test(name);

const isCurrentAddressField = (name: string) =>
  /^addressCoApplicant\d+current/i.test(name) && ! /^addressCoApplicant\d+currentsameAsPermanent$/i.test(name);
const isSameAsPermanentField = (name: string) =>
  /^addressCoApplicant\d+currentsameAsPermanent$/i.test(name);

const isEmploymentIsEmployedField = (name: string) =>
  /^employmentCoApplicant\d+(employmentisEmployed|isEmployed)$/i.test(name);

const getCoApplicantIsEmployedValue = (
  applicantNumber: string | null,
  vals: Record<string, any>,
): string => {
  if (!applicantNumber) return "";

  const keyA = `employmentCoApplicant${applicantNumber}employmentisEmployed`;
  const keyB = `employmentCoApplicant${applicantNumber}isEmployed`;

  const raw = vals?.[keyA] ?? vals?.[keyB];
  const normalized = raw === true ? "Yes" : String(raw ?? "");
  if (!normalized) return "";
  if (normalized.toLowerCase() === "yes" || normalized === "1") return "Yes";
  if (normalized.toLowerCase() === "no" || normalized === "0") return "No";
  return normalized;
};


const isEmploymentTypeField = (name: string) =>
  /^employmentCoApplicant\d+employmentType$/i.test(name);

const isEmploymentCompanyHeaderCandidate = (name: string) =>
  /^employmentCoApplicant\d+company|^employmentCoApplicant\d+companyName|^employmentCoApplicant\d+designation|^employmentCoApplicant\d+monthlyObligation|^employmentCoApplicant\d+monthlyIncome|^employmentCoApplicant\d+employmentYear|^employmentCoApplicant\d+employmentMonth/i.test(name);


const isCoApplicantPayPartyField = (name: string): boolean =>
  /^basicCoApplicant\d+PayParty$/i.test(name);

// Monthly Obligation and Monthly Income must stay mandatory for every employment
// type (Farmer, Others, Salaried, SEP, SENP), regardless of what the field config says.
const isMandatoryEmploymentAmountField = (name: string): boolean =>
  /^employmentCoApplicant\d+monthlyObligation$/i.test(name) ||
  /^employmentCoApplicant\d+monthlyIncome$/i.test(name);

const withMandatoryEmploymentAmount = (field: any) =>
  isMandatoryEmploymentAmountField(getFieldName(field))
    ? { ...field, required: true }
    : field;

// Company address (address lines, landmark, pincode) becomes mandatory once the
// co-applicant's employment type is Salaried/SEP/SENP, even though the field
// config marks these fields optional by default.
const MANDATORY_COMPANY_ADDRESS_EMPLOYMENT_TYPES = ["Salaried", "SEP", "SENP"];

const COMPANY_ADDRESS_FIELD_REGEX =
  /^employmentCoApplicant(\d+)(companyAddress1|companyAddress2|companyAddress3|landmark|pincode|city|state|country)$/i;

const withMandatoryCompanyAddress = (field: any, vals: Record<string, any>) => {
  const match = getFieldName(field).match(COMPANY_ADDRESS_FIELD_REGEX);
  if (!match) return field;

  const coAppNum = match[1];
  const employmentType = vals?.[`employmentCoApplicant${coAppNum}employmentType`];
  return MANDATORY_COMPANY_ADDRESS_EMPLOYMENT_TYPES.includes(employmentType)
    ? { ...field, required: true }
    : field;
};

// Company Name is mandatory for Salaried/SEP/SENP and optional otherwise,
// regardless of what the field config says (older co-applicants may still
// have `required: true` persisted).
const COMPANY_NAME_FIELD_REGEX = /^employmentCoApplicant(\d+)companyName$/i;

const withOptionalCompanyName = (field: any, vals: Record<string, any>) => {
  const match = getFieldName(field).match(COMPANY_NAME_FIELD_REGEX);
  if (!match) return field;

  const employmentType = vals?.[`employmentCoApplicant${match[1]}employmentType`];
  return {
    ...field,
    required: MANDATORY_COMPANY_ADDRESS_EMPLOYMENT_TYPES.includes(employmentType),
  };
};

const isBasicVerificationField = (name: string): boolean =>
  /^basicCoApplicant\d+(AadhaarVerified|IsPanVerified|IsMobileVerified|IsEmailVerified|AadhaarNumber)$/i.test(
    name,
  );

const getSectionFromFields = (
  fields: any[],
): "basic" | "address" | "employment" => {
  const names = (fields || []).map(getFieldName);
  if (names.some((name) => isEmploymentField(name))) return "employment";
  if (names.some((name) => isAddressField(name))) return "address";
  return "basic";
};

const SingleCoApplicantAdditionalDetails: React.FC<
  SingleCoApplicantAdditionalDetailsProps
> = ({ fields = [], renderField, values = {},formikRef }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { trackingId, isDocumentVerifiedCoApplicant } = useSelector(
    (state: RootState) => state.stepper,
  );
const [isAadharLoading, setIsAadharLoading] = useState<
    Record<string, boolean>
  >({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "error" | "success";
  }>({
    open: false,
    message: "",
    severity: "error",
  });

  const coApplicantNumber = useMemo(
    () => getCoApplicantNumberFromFields(fields),
    [fields],
  );

  useEffect(() => {
    if (isFirstCoApplicant(coApplicantNumber)) {
      ensureFirstCoApplicantEmploymentYes(formikRef);
    }
  }, [coApplicantNumber, formikRef]);

  // Determine which page section this “single co-applicant summary editor” is showing.
  // In your FormEdit usage, this component is rendered only for SUMMARY edit mode
  // at a co-applicant related vertical step (address or employment).
  const section = useMemo(() => getSectionFromFields(fields), [fields]);
  const coAppKey = coApplicantNumber
    ? `CoApplicant${coApplicantNumber}`
    : "CoApplicant1";
  const basicField = (n: string | number, suffix: string) =>
    `basicCoApplicant${n}${suffix}`;


  // “Specify Relationship” logic for Others selection.
  // Some co-applicant basic fields are rendered conditionally in the edit flow,
  // but this SUMMARY renderer needs to hide/show the dependent field too.
  const coApplicantNumberForSpecifyRelation = coApplicantNumber;
  const relationFieldName = coApplicantNumberForSpecifyRelation
    ? `basicCoApplicant${coApplicantNumberForSpecifyRelation}RelationWithStudent`
    : "";
  const specifyRelationFieldName = coApplicantNumberForSpecifyRelation
    ? `basicCoApplicant${coApplicantNumberForSpecifyRelation}OtherRelationWithStudent`
    : "";
  const relationValue = coApplicantNumberForSpecifyRelation
    ? values?.[relationFieldName]
    : undefined;
  const shouldShowSpecifyRelation = relationValue === "OTHERS" ||
    String(relationValue ?? "").toUpperCase() === "OTHERS" || relationValue === "Farmer" ||
    String(relationValue ?? "").toUpperCase() === "Farmer";


  const filteredFields = useMemo(() => {
    return (fields || []).filter((field: any) => {
      const name = getFieldName(field);
      if (!name) return false;

      // SUMMARY edit rule: hide PayParty section for Co-Applicant 1,
      // but allow it for Co-Applicant 2+.
      if (name === "basicCoApplicant1PayParty") {
        return false;
      }

      if (
        coApplicantNumber &&
        !new RegExp(`CoApplicant${coApplicantNumber}`, "i").test(name)
      ) {
        return false;
      }

      if (section === "address") return isAddressField(name);
      if (section === "employment") return isEmploymentField(name);

      // Basic section: hide verification fields (handled by dedicated UI in the CoApplicant flow)
      if (isBasicVerificationField(name)) return false;

      // “Specify Relationship” visibility rule:
      // only show OtherRelationWithStudent field when RelationshipWithStudent is OTHERS.
      if (
        shouldShowSpecifyRelation === false &&
        specifyRelationFieldName &&
        name === specifyRelationFieldName
      ) {
        return false;
      }
      if (
        shouldShowSpecifyRelation === true &&
        relationFieldName &&
        name === relationFieldName
      ) {
        return true;
      }

      return !isAddressField(name) && !isEmploymentField(name);
    });
  }, [fields, coApplicantNumber, section, shouldShowSpecifyRelation, specifyRelationFieldName, relationFieldName]);


  // Address parity: mimic CoApplicantAdditionalDetails layout with
  // Permanent Address header + fields, sameAsPermanent field, Current Address header + fields.
  if (section === "address") {
    const permanentFields = filteredFields.filter((f: any) =>
      isPermanentAddressField(getFieldName(f)),
    );
    const currentFields = filteredFields.filter((f: any) =>
      isCurrentAddressField(getFieldName(f)),
    );
    const sameAsField =
      filteredFields.find((f: any) => isSameAsPermanentField(getFieldName(f))) ||
      null;

    return (
      <Grid container mt={2} spacing={2} sx={{ mb: 2, ml: 2 }}>
        {permanentFields.length > 0 && (
          <Grid item xs={12}>
            {/* Keep it simple: section headers are visual only */}
            <Grid container alignItems="center" sx={{ mb: 1 }}>
              <Grid item>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Permanent Address
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        )}

        {permanentFields.map((field: any, idx: number) => (
          <Grid item xs={12} sm={6} md={4} key={field?.id || field?.name || idx}>
            {renderField(field) as unknown as React.ReactNode}
          </Grid>
        ))}

        {sameAsField && (
          <Grid item xs={12}>
            {renderField(sameAsField) as unknown as React.ReactNode}
          </Grid>
        )}


        {currentFields.length > 0 && (
          <Grid item xs={12}>
            <Grid container alignItems="center" sx={{ mb: 1 }}>
              <Grid item>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Current Address
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        )}

        {currentFields.map((field: any, idx: number) => (
          <Grid item xs={12} sm={6} md={4} key={field?.id || field?.name || idx}>
            {renderField(field) as unknown as React.ReactNode}
          </Grid>
        ))}
      </Grid>
    );
  }

  // Employment parity: mimic CoApplicantAdditionalDetails layout
  if (section === "employment") {
    const employedField = filteredFields.find((f: any) =>
      isEmploymentIsEmployedField(getFieldName(f)),
    );

    const isFirstCoApplicantEmployment = isFirstCoApplicant(coApplicantNumber);

    const isEmployedValue = isFirstCoApplicantEmployment
      ? "Yes"
      : getCoApplicantIsEmployedValue(
          coApplicantNumber,
          values,
        );

    const employmentTypeValue = coApplicantNumber
      ? values?.[`employmentCoApplicant${coApplicantNumber}employmentType`]
      : undefined;
    const selectedEmploymentType = getAutocompleteOptionValue(employmentTypeValue);

    const isEmploymentTypeOther =
      String(selectedEmploymentType ?? "")
        .trim()
        .toUpperCase() === "OTHER" ||
      String(selectedEmploymentType ?? "")
        .trim()
        .toUpperCase() === "OTHERS" ||  String(selectedEmploymentType ?? "")
        .trim()
        .toUpperCase() === "FARMER" ;

    const employmentFields = filteredFields.filter((f: any) =>
      /^employmentCoApplicant\d+/.test(getFieldName(f)),
    );

    const employmentTypeField =
      coApplicantNumber &&
      employmentFields.find(
        (f: any) =>
          getFieldName(f) ===
            `employmentCoApplicant${coApplicantNumber}employmentType` ||
          /^employmentCoApplicant\d+employmentType$/i.test(getFieldName(f)),
      );

    const monthlyFinancialCommitmentsField =
      coApplicantNumber &&
      employmentFields.find(
        (f: any) =>
          getFieldName(f) ===
            `employmentCoApplicant${coApplicantNumber}monthlyObligation` ||
          /^employmentCoApplicant\d+monthlyObligation$/i.test(getFieldName(f)),
      );

    const monthlyIncomeField =
      coApplicantNumber &&
      employmentFields.find(
        (f: any) =>
          getFieldName(f) ===
            `employmentCoApplicant${coApplicantNumber}monthlyIncome` ||
          /^employmentCoApplicant\d+monthlyIncome$/i.test(getFieldName(f)),
      );
    return (
      <Grid container mt={2} spacing={2} sx={{ mb: 2, ml: 2 }}>
        {employedField && (
          <Grid item xs={12}>
            {isFirstCoApplicantEmployment && (
              <FirstCoApplicantEmploymentNotice />
            )}
            {renderField(
              lockFirstCoApplicantEmploymentField(employedField),
            ) as unknown as React.ReactNode}
          </Grid>
        )}

        {isEmployedValue === "Yes" && (
          <>
            {!selectedEmploymentType ? (
              <>
                {employmentTypeField && (
                  <Grid item xs={12} sm={3} md={4}>
                    {renderField(
                      employmentTypeField,
                    ) as unknown as React.ReactNode}
                  </Grid>
                )}
              </>
            ) : isEmploymentTypeOther ? (
              <>
                {/* Show Employment Type + only the 2 required fields for OTHER/OTHERS */}
                {employmentTypeField && (
                  <Grid item xs={12} sm={3} md={4}>
                    {renderField(
                      employmentTypeField,
                    ) as unknown as React.ReactNode}
                  </Grid>
                )}
                {monthlyFinancialCommitmentsField && (
                  <Grid item xs={12} sm={3} md={4}>
                    {renderField(
                      withMandatoryEmploymentAmount(monthlyFinancialCommitmentsField),
                    ) as unknown as React.ReactNode}
                  </Grid>
                )}
                {monthlyIncomeField && (
                  <Grid item xs={12} sm={3} md={4}>
                    {renderField(
                      withMandatoryEmploymentAmount(monthlyIncomeField),
                    ) as unknown as React.ReactNode}
                  </Grid>
                )}
              </>
            ) : (
              <>
                {(() => {
                  let companyHeaderShown = false;

                  return employmentFields.map((field: any, idx: number) => {
                    const name = getFieldName(field);

                    // skip the radio if already rendered
                    if (employedField && name === getFieldName(employedField)) {
                      return null;
                    }

                    const shouldInsertCompanyHeader =
                      !companyHeaderShown &&
                      /company/i.test(name) &&
                      name !== `employmentCoApplicant${coApplicantNumber}companyName`;

                    if (shouldInsertCompanyHeader) {
                      companyHeaderShown = true;
                    }

                    const isYearField = /^employmentCoApplicant\d+employmentYear$/i.test(name);
                    const isMonthField = /^employmentCoApplicant\d+employmentMonth$/i.test(name);

                    // Requirement: render employment inputs in 4-col layout (no gaps)
                    // for both OTHER and non-OTHER cases.
                    const colMd = 4;

                    return (
                      <React.Fragment
                        key={field?.id || field?.name || idx}
                      >
                        {shouldInsertCompanyHeader && (
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600 }}
                            >
                              Company Address
                            </Typography>
                          </Grid>
                        )}
                        <Grid
                          item
                          xs={12}
                          sm={3}
                          md={4}
                        >
                          {renderField(
                            withOptionalCompanyName(
                              withMandatoryCompanyAddress(
                                withMandatoryEmploymentAmount(field),
                                values,
                              ),
                              values,
                            ),
                          ) as unknown as React.ReactNode}
                        </Grid>
                      </React.Fragment>
                    );
                  });
                })()}
              </>
            )}
          </>
        )}

        {/* When employedValue is No, keep only required isEmployed + employmentType (if provided) */}
        {/* {isEmployedValue !== "Yes" && employmentTypeField && (
          <Grid item xs={12}>
            {renderField(employmentTypeField) as unknown as React.ReactNode}
          </Grid>
        )} */}
      </Grid>
    );
  }

  // fallback: basic section
  return (
    <>
      <Grid container mt={2} spacing={2} sx={{ mb: 2, ml: 2 }}>
        {filteredFields.map((field: any, idx: number) => {
          const name = getFieldName(field);
          const isPayParty = isCoApplicantPayPartyField(name);

          return (
            <Grid
              item
              xs={12}
              sm={isPayParty ? 12 : 4}
              md={isPayParty ? 12 : 4}
              key={field?.id || field?.name || idx}
            >
              {renderField(
                // Specify Relationship is mandatory whenever it's shown.
                name === specifyRelationFieldName ? { ...field, required: true } : field,
              ) as unknown as React.ReactNode}
            </Grid>
          );
        })}
      </Grid>

      {/* <Grid container className="inner-form-box" mt={2} ml={4}>
          <Grid item xs={12} sm={12} md={12}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <img
                src={idIcon}
                alt="icon"
                style={{ width: 20, height: 20 }}
              />
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#4B465C",
                }}
              >
                Verify Aadhaar Number
              </Typography>
            </Box>
            <label
              className="text-lable"
              style={{ marginBottom: "10px" }}
            >
              Verify your Aadhaar to enhance processing speed and accuracy. This streamlined step is provided for your ease and remains optional, allowing you to enter details manually if you prefer.
            </label>
          </Grid>
          {isAadharLoading[coAppKey] ? (
            <Skeleton
              variant="rectangular"
              width={120}
              height={40}
              sx={{ borderRadius: 2, mb: 1 }}
            />
          ) : (
            <IconButton
              sx={{
                backgroundColor:
                  formikRef?.current?.values?.[
                    `basicCoApplicant${coAppKey.replace(
                      "CoApplicant",
                      ""
                    )}AadhaarVerified`
                  ] === true ||
                    isDocumentVerifiedCoApplicant[coAppKey]
                    ? "#33c2c4"
                    : undefined,
                color:
                  formikRef?.current?.values?.[
                    `basicCoApplicant${coAppKey.replace(
                      "CoApplicant",
                      ""
                    )}AadhaarVerified`
                  ] === true ||
                    isDocumentVerifiedCoApplicant[coAppKey]
                    ? "#33c2c4"
                    : "#000",
                border:
                  formikRef?.current?.values?.[
                    `basicCoApplicant${coAppKey.replace(
                      "CoApplicant",
                      ""
                    )}AadhaarVerified`
                  ] === true ||
                    isDocumentVerifiedCoApplicant[coAppKey]
                    ? "1px solid #33c2c4"
                    : "",
                "&.Mui-disabled": {
                  backgroundColor: formikRef?.current?.values?.[
                    `basicCoApplicant${coAppKey.replace(
                      "CoApplicant",
                      ""
                    )}AadhaarVerified`
                  ] === true ||
                    isDocumentVerifiedCoApplicant[coAppKey]
                    ? "white !important"
                    : "lightgray !important",
                  color:
                    formikRef?.current?.values?.[
                      `basicCoApplicant${coAppKey.replace(
                        "CoApplicant",
                        ""
                      )}AadhaarVerified`
                    ] === true ||
                      isDocumentVerifiedCoApplicant[coAppKey]
                      ? "#33c2c4"
                      : "#000",
                },
              }}
              onClick={async () => {
                // Find co-applicant values for this tab
                const coAppNum = coAppKey.replace(
                  "CoApplicant",
                  ""
                );
                const values =
                  formikRef?.current?.values || {};
                const getField = (suffix: string) =>
                  values[
                  `basicCoApplicant${coAppNum}${suffix}`
                  ] || "";
                const email = getField("Email");
                const firstName = getField("FirstName");
                const lastName = getField("LastName");
                const phone_number =
                  getField("PhoneNumber");
                setIsAadharLoading((prev) => ({
                  ...prev,
                  [coAppKey]: true,
                }));
                try {

                  const panNumber = getField("PanNumber");
                  const aadhaarPayload: any = {
                    applicantEmail: email?.toLowerCase(),
                    applicantName: `${firstName || ""} ${lastName || ""
                      }`.trim(),
                    applicantMobile: phone_number,
                    applicantPan: panNumber,
                    redirectUrl:`${window.location.origin}/application/loan-summary`,
                    landingPageNotRequired: true,
                    trackingId: trackingId,
                    typeOfApplicant: `Co-Applicant ${coAppNum}`,
                  };

                  const resp = await postsServices.addPost(
                    "api/v1/applicant/create-app-via-aadhaar-phone",
                    aadhaarPayload
                  );

                  if (
                    resp.data?.status === "success" &&
                    resp.data.data?.link
                  ) {

                    let link = resp.data.data.link;
                    const applicationNo =
                      resp.data.data.applicationNo;
                    localStorage.setItem(
                      "applicationNo",
                      applicationNo
                    );
                    localStorage.setItem(
                      "aadhaarCoAppKey",
                      coAppKey
                    );
                    dispatch(
                      setAadhaarApplicationNoCoApplicant({
                        coAppKey,
                        applicationNo,
                      })
                    );
                    // Optionally: dispatch(setAadhaarApplicationNo(applicationNo));
                    if (!/^https?:\/\//i.test(link)) {
                      link = "https://" + link;
                    }

                    // await trackCustomerJourneyEventAndWait(
                    //   `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                    //   decrypt(userProfile?.id) ?? "",
                    //   trackingId,
                    //   {
                    //     label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                    //     category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                    //     action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                    //     page: `ProvisionalOff_Loan Details_COAPP(${coAppNum}) Aadhaar Verification`,
                    //   },
                    //   500
                    // );

                    window.location.replace(link);
                  } else {
                    setSnackbar({
                      open: true,
                      message: resp.data?.message,
                      severity: "success",
                    });
                    // dispatch(
                    //   setStatusPopup({
                    //     status: "info",
                    //     message: resp.data?.message,
                    //   })
                    // );
                  }
                } catch (err: any) {
                  setSnackbar({
                    open: true,
                    message: err?.response?.data?.message,
                    severity: "error",
                  });
                  // dispatch(
                  //   setStatusPopup({
                  //     status: "info",
                  //     message: err?.response?.data?.message,
                  //   })
                  // );
                } finally {
                  setIsAadharLoading((prev) => ({
                    ...prev,
                    [coAppKey]: false,
                  }));
                }
              }}
              disabled={(() => {
                const coAppNum = coAppKey.replace("CoApplicant", "");
                // Already verified — disable
                const isAadhaarVerified =
                  formikRef?.current?.values?.[`basicCoApplicant${coAppNum}AadhaarVerified`] === true ||
                  isDocumentVerifiedCoApplicant[coAppKey];
                // PAN must be verified (previous validation)
                const isPanNotVerified =
                  formikRef?.current?.values?.[`basicCoApplicant${coAppNum}IsPanVerified`] === "" ||
                  formikRef?.current?.values?.[`basicCoApplicant${coAppNum}IsPanVerified`] === false;
                // Mobile must be exactly 10 digits (new validation)
                const mobileVal: string =
                  formikRef?.current?.values?.[`basicCoApplicant${coAppNum}PhoneNumber`] ?? "";
                const isMobileInvalid = mobileVal.replace(/\D/g, "").length !== 10;
                // Email must be valid format (new validation)
                const emailVal: string =
                  formikRef?.current?.values?.[`basicCoApplicant${coAppNum}Email`] ?? "";
                const emailRegex = JOURNEY_EMAIL_REGEX;
                const isEmailInvalid = !emailRegex.test(emailVal);
                return isAadhaarVerified || isPanNotVerified || isMobileInvalid || isEmailInvalid;
              })()}
            >
              {formikRef?.current?.values?.[
                `basicCoApplicant${coAppKey.replace(
                  "CoApplicant",
                  ""
                )}AadhaarVerified`
              ] === true ||
                isDocumentVerifiedCoApplicant[coAppKey] ? (
                <img
                  src={VerifiedIcon}
                  alt="arrow-narrow-right"
                />
              ) : (
                <></>
              )}

              {`${formikRef?.current?.values?.[
                `basicCoApplicant${coAppKey.replace(
                  "CoApplicant",
                  ""
                )}AadhaarVerified`
              ] === true ||
                isDocumentVerifiedCoApplicant[coAppKey]
                ? "Verified"
                : "Verify Via Digilocker"
                }`}

              {!(
                formikRef?.current?.values?.[
                `basicCoApplicant${coAppKey.replace("CoApplicant", "")}AadhaarVerified`
                ] === true || isDocumentVerifiedCoApplicant[coAppKey]
              ) && <img src={RightBlackArrow} alt="right-arrow" />
              }
            </IconButton>
          )}
        </Grid> */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};


export default SingleCoApplicantAdditionalDetails;
